import { ArrowRight, ChevronDown, ChevronRight, CircleCheckBig, CircleX, FilePen, GitMerge, Hand, Hourglass, MessageCircleQuestion, MessageSquareReply, ServerOff, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import type { ComponentType } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { watchStale } from '@orgus/protocol';
import type { Incident } from '@orgus/protocol';
import { BUTTON_SIZE, BUTTON_VARIANT, EmptyState, ErrorState, IconTile, GROUP_INDENT, LiveNumber, Panel, Pressable, REVEAL_GROUP, RelativeTime, RevealRest, RevealSlot, cn, formatDuration, formatInt, plural, useNow } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useSessionParam } from '../sessions/useSessionParam.ts';
import { AssignControl } from '../teams/AssignControl.tsx';
import { useLanding } from '../landing/api.ts';
import { computeNeedsYou } from './needsYou.ts';
import { usePendingApprovals } from '../approvals/live.ts';
import { withApprovals } from '../approvals/needs.ts';
import { openAnswer } from '../approvals/AnswerSheet.tsx';
import { RiskLabel } from '../approvals/RiskLabel.tsx';
import { useBlockedApprovals } from '../approvals/blocked.ts';
import { useWaitingCount } from '../approvals/waiting.ts';
import { ASK_GROUP_ORDER } from '../approvals/decision.ts';
import { withBlocked } from '../approvals/blockedNeeds.ts';
import { useAssistantNeedItems } from '../assistant/needs.ts';
import type { NeedItem, NeedKind } from './needsYou.ts';
import { BUDGET_NEED_KIND, useBudgetNeedItems } from '../costs/needs.ts';
import type { BudgetNeedItem, BudgetNeedKind } from '../costs/needs.ts';
import { SECURITY_NEED_KIND, useSecurityNeedItems } from '../audit-ops/needs.ts';
import type { SecurityNeedItem, SecurityNeedKind } from '../audit-ops/needs.ts';
import { useProjectProtocol } from '../project-health/api.ts';
import { problemNeeds } from '../project-health/needs.ts';
import { orgusIsFixing } from '../project-health/model.ts';
import type { ProblemNeedItem } from '../project-health/needs.ts';
import { ProblemRowActions, useProblemParam } from '../project-health/Problems.tsx';
import { IncidentRow, WatchCaption, useIncidentActor } from '../watch/IncidentRow.tsx';
import { attentionRows, orphanTiers, splitHandled } from '../watch/model.ts';
import { dedupeWithIncidents } from '../watch/needs.ts';
import { useWatch } from '../watch/useWatch.ts';
import { GROUP_ROWS, NEED_GROUP_LABEL, linkedRow, needsGroups, rowCount } from './needsGroups.ts';
import type { NeedGroup, NeedsRow } from './needsGroups.ts';

/** The groups the Inbox lists (what only a person can decide), against the alerts and problems. */
const ASK_GROUPS: ReadonlySet<NeedGroup> = new Set(ASK_GROUP_ORDER);

type Need = NeedItem | BudgetNeedItem | SecurityNeedItem | ProblemNeedItem;
type Tone = 'attention' | 'danger' | 'neutral';
type Icon = ComponentType<{ className?: string }>;

/** The colour of a kind defined elsewhere (budget, security), read from its legacy class. */
const toneOfClass = (cls: string): Tone => (/\bfailed\b|failed-/.test(cls) ? 'danger' : /waiting/.test(cls) ? 'attention' : 'neutral');

type KindMeta = { icon: Icon; tone: Tone; label: string };
const borrowed = (m: Record<string, { icon: Icon; cls: string; label: string }>): Record<string, KindMeta> =>
  Object.fromEntries(Object.entries(m).map(([k, v]) => [k, { icon: v.icon, tone: toneOfClass(v.cls), label: v.label }]));

const KIND = {
  ...(borrowed(SECURITY_NEED_KIND) as Record<SecurityNeedKind, KindMeta>),
  ...(borrowed(BUDGET_NEED_KIND) as Record<BudgetNeedKind, KindMeta>),
  session: { icon: Hand, tone: 'attention', label: 'Needs you' },
  asked: { icon: MessageCircleQuestion, tone: 'neutral', label: 'Asked' },
  agent: { icon: Hourglass, tone: 'attention', label: 'Looks stuck' },
  landing: { icon: GitMerge, tone: 'attention', label: 'Landing needs you' },
  pipeline: { icon: CircleX, tone: 'danger', label: 'Failed' },
  machine: { icon: ServerOff, tone: 'neutral', label: 'Not reporting' },
  worktree: { icon: FilePen, tone: 'neutral', label: 'Uncommitted work' },
  'problem-critical': { icon: CircleX, tone: 'danger', label: 'Critical' },
  'problem-warning': { icon: TriangleAlert, tone: 'attention', label: 'Warning' },
} satisfies Record<NeedKind | BudgetNeedKind | SecurityNeedKind | ProblemNeedItem['kind'], KindMeta>;


export function useNeedsYou(): Need[] {
  const now = useNow(10_000);
  // Loads the landing queue's state when the hub did not send it with the first state (and demo data in demo mode).
  useLanding();
  const e = useOrgus(
    useShallow((s) => ({ sessions: s.sessions, pipelines: s.pipelines, machines: s.machines, checkouts: s.checkouts, projects: s.projects, ext: s.ext })),
  );
  const pending = usePendingApprovals();
  const blocked = useBlockedApprovals().items;
  const budget = useBudgetNeedItems();
  const security = useSecurityNeedItems();
  const assistant = useAssistantNeedItems();
  const pp = useProjectProtocol().state;
  return useMemo(() => {
    // Security alerts first (a leaked credential outranks a waiting session), then critical
    // problems (project and Orgus checks), then approvals and sessions waiting on an answer
    // (blocked approvals first), then assistant steps waiting for approval, then budget and
    // runaway rows, then warning problems, then the rest. A problem that repeats a row is said once.
    const all = withBlocked(withApprovals(computeNeedsYou(e, now), pending, e), blocked, e);
    // What Orgus is fixing on its own does not need you; it comes back as a problem if Orgus gives up (ADR 0064).
    const open = (pp?.problems ?? []).filter((p) => !orgusIsFixing(p.key, pp?.dispatches));
    const { core, critical, warning } = problemNeeds(all, open, Object.values(e.checkouts), (id) => e.projects[id]?.name);
    // Asked questions (nothing held) come after everything that blocks (ADR 0056).
    const waiting = core.filter((i) => i.kind === 'session');
    const asked = core.filter((i) => i.kind === 'asked');
    const rest = core.filter((i) => i.kind !== 'session' && i.kind !== 'asked');
    return [...security, ...critical, ...waiting, ...assistant, ...asked, ...budget, ...warning, ...rest];
  }, [blocked, security, budget, assistant, e, now, pending, pp]);
}

function Row({ item, place }: { item: Need; place: string | null }) {
  const navigate = useNavigate();
  const [, openSession] = useSessionParam();
  const [, openProblem] = useProblemParam();
  const k = KIND[item.kind];
  const Icon = k.icon;
  // Two quiet facts under the title, each whole: a request's risk (its priority) or where the row
  // lives (else what it is), then its age. The detail sentence is the row's tooltip, never a cut line.
  const risk = 'order' in item ? item.order?.risk : undefined;
  const first = risk ? null : (place ?? k.label);
  // A command or a path (one long word) breaks anywhere, so it fills the first line instead of
  // dropping whole to the second; words still wrap between words.
  const longWord = /\S{20,}/.test(item.title);

  const answer = item.verb === 'Answer';
  return (
    <li className={cn(REVEAL_GROUP, 'motion-arrive relative flex min-h-row-2line items-center rounded-control transition-colors duration-(--duration-fast) hover:bg-hover')} data-row>
      <Pressable
        onClick={() => ('problem' in item.to ? openProblem(item.to.problem) : 'session' in item.to ? openSession(item.to.session) : 'approval' in item.to ? openAnswer(item.to.approval) : navigate(item.to.path))}
        className="group flex h-full min-h-row-2line min-w-0 flex-1 items-center gap-3 rounded-control px-3 py-2"
        title={[item.title, item.detail, risk ? place : null].filter(Boolean).join(' · ') || undefined}
      >
        {/* On a phone the group heading says what kind it is: the tile gives its width to the title. */}
        <span className="flex shrink-0 max-sm:hidden" role="img" aria-label={k.label}>
          <IconTile icon={<Icon />} tone={k.tone} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          {/* The title is the key fact: two lines before it is cut. */}
          <span className={cn('line-clamp-2 text-ui font-medium text-fg', longWord ? 'break-all' : 'wrap-anywhere')}>{item.title}</span>
          <span className="flex min-w-0 items-center gap-2 whitespace-nowrap text-caption text-fg-subtle">
            {risk ? (
              <RiskLabel risk={risk} className="shrink-0" />
            ) : (
              first && (
                <span className="min-w-0 truncate" data-meta>
                  {first}
                </span>
              )
            )}
            {item.at != null && (
              <span className="shrink-0 num" data-meta>
                <RelativeTime ts={item.at} />
              </span>
            )}
          </span>
        </span>
        {/* The verb, drawn as the one small secondary button (Live now's Answer is the same). The
            hover actions take the place beside it: it gives way, it is never drawn over. */}
        <RevealRest className={cn(BUTTON_VARIANT.secondary, BUTTON_SIZE.sm, 'whitespace-nowrap group-hover:border-border-strong')}>
          {answer && <MessageSquareReply className="max-sm:hidden" aria-hidden />}
          {item.verb}
        </RevealRest>
      </Pressable>
      <RevealSlot className="absolute right-3 flex items-center gap-1">
        {'problem' in item && <ProblemRowActions problem={item.problem} />}
        <AssignControl itemKey={item.key} title={item.title} projectId={item.projectId ?? null} />
      </RevealSlot>
    </li>
  );
}

/** What every row in the panel shares: the clock, who is signed in, which incidents are open. */
interface Shared {
  places: Map<string, string | null>;
  now: number;
  me: string | null;
  can: boolean;
  open: Set<string>;
  toggle: (id: string) => void;
  linked: string | null;
}

function Line({ row, x }: { row: NeedsRow<Need>; x: Shared }) {
  if (row.type === 'need') return <Row item={row.need} place={x.places.get(row.need.key) ?? null} />;
  const i = row.incident;
  return <IncidentRow incident={i} now={x.now} me={x.me} can={x.can} expanded={x.open.has(i.id)} onToggle={() => x.toggle(i.id)} linked={x.linked === i.id} />;
}

function Group({ group, rows, all, onAll, x }: { group: NeedGroup; rows: NeedsRow<Need>[]; all: boolean; onAll: (all: boolean) => void; x: Shared }) {
  const shown = all ? rows : rows.slice(0, GROUP_ROWS);
  const oldest = rows.reduce<number | null>((m, r) => {
    const at = r.type === 'need' ? r.need.at : r.incident.openedAt;
    return at != null && (m == null || at < m) ? at : m;
  }, null);
  const id = 'needs-' + group;
  return (
    <section aria-labelledby={id} data-need-group={group} className="flex min-w-0 flex-col">
      <h3 id={id} className="flex h-8 items-center gap-2 px-3 text-caption text-fg-subtle">
        <span className="font-medium text-fg-muted">{NEED_GROUP_LABEL[group]}</span>
        <span className="num">{formatInt(rows.length)}</span>
        {oldest != null && <span className="ml-auto num">oldest {formatDuration(x.now - oldest)}</span>}
      </h3>
      {/* The rows sit one group step in under their heading (GROUP_INDENT). */}
      <div className={cn('flex min-w-0 flex-col', GROUP_INDENT, 'max-sm:pl-0')}>
        <ul className="flex flex-col" data-list aria-label={NEED_GROUP_LABEL[group]}>
          {shown.map((r) => (
            <Line key={r.key} row={r} x={x} />
          ))}
        </ul>
        {/* A quiet fold, like the transcript's Show more: the group opens in place. */}
        {rows.length > GROUP_ROWS && (
          <Pressable
            onClick={() => onAll(!all)}
            aria-expanded={all}
            className="mx-3 my-1 flex w-auto items-center gap-1 self-start rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
          >
            {all ? 'Show less' : `Show ${formatInt(rows.length - GROUP_ROWS)} more`}
            <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', all && 'rotate-180')} aria-hidden />
          </Pressable>
        )}
      </div>
    </section>
  );
}

/**
 * What Orgus handles itself (ORGU-92): folded under the groups, never counted. Each row says what
 * Orgus does (a mission, a removal, the landing queue's wake) and opens to the mission.
 */
function Handled({ incidents, open, onOpen, x }: { incidents: Incident[]; open: boolean; onOpen: (open: boolean) => void; x: Shared }) {
  const tiers = useMemo(() => orphanTiers(incidents), [incidents]);
  return (
    <section aria-label="Handled by Orgus" data-need-group="handled" className="flex min-w-0 flex-col px-1 pb-1">
      <Pressable
        onClick={() => onOpen(!open)}
        aria-expanded={open}
        className="flex h-8 w-full items-center gap-2 rounded-control px-3 text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
      >
        <ChevronRight className={cn('size-3 transition-transform duration-(--duration-fast)', open && 'rotate-90')} aria-hidden />
        <span className="font-medium">Handled by Orgus</span>
        <span className="num">{formatInt(incidents.length)}</span>
      </Pressable>
      {open && tiers.length > 0 && (
        <ul className={cn('flex flex-wrap items-center gap-1 pb-1', GROUP_INDENT)} aria-label="Orphaned work by tier">
          {tiers.map((t) => (
            <li key={t.tier} title={t.hint} data-tier={t.tier} className="flex h-6 items-center gap-2 rounded-control bg-surface px-2 text-caption text-fg-subtle">
              <span>{t.label}</span>
              <span className="num text-fg-muted">{formatInt(t.count)}</span>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <ul className={cn('flex flex-col', GROUP_INDENT)} data-list aria-label="Handled by Orgus">
          {incidents.map((i) => (
            <IncidentRow key={i.id} incident={i} now={x.now} me={x.me} can={x.can} expanded={x.open.has(i.id)} onToggle={() => x.toggle(i.id)} linked={x.linked === i.id} />
          ))}
        </ul>
      )}
    </section>
  );
}

/** Rows shown at rest across the panel: whole groups fill it, the rest fold behind one quiet line. */
const ROW_BUDGET = 8;

/** The groups that fit the budget whole (always the first), and the ones folded after them. */
function restGroups<G extends { rows: unknown[] }>(groups: G[]): { shown: G[]; folded: G[] } {
  let used = 0;
  let n = 0;
  for (const g of groups) {
    const rows = Math.min(g.rows.length, GROUP_ROWS);
    if (n > 0 && used + rows > ROW_BUDGET) break;
    used += rows;
    n++;
  }
  return { shown: groups.slice(0, n), folded: groups.slice(n) };
}

const toggled = <T,>(s: Set<T>, v: T, on: boolean): Set<T> => {
  if (s.has(v) === on) return s;
  const n = new Set(s);
  if (on) n.add(v);
  else n.delete(v);
  return n;
};

/**
 * Everything that needs a person, in one place (ADR 0058 decision 3, as amended): the decisions
 * first (security, blocked, approvals, questions), then the problem groups, where what Orgus
 * Watch found wrong ranks beside the rows read from live state, critical first. A thing both
 * describe is said once: by the incident while the watch runs (it has the action and the
 * evidence), by the live row while the watch is stale.
 */
export function NeedsYou({ items, className }: { items: Need[]; className?: string }) {
  const projects = useOrgus((s) => s.projects);
  const now = useNow(30_000);
  const view = useWatch();
  const watch = view.kind === 'ready' ? view.state : null;
  const stale = watch != null && watchStale(watch, now);
  const { me, can } = useIncidentActor();
  const [params] = useSearchParams();
  const linked = params.get('incident');
  const shown = useMemo(() => dedupeWithIncidents(items, attentionRows(watch), stale ? 'derived' : 'incident'), [items, watch, stale]);
  // Where each row lives, said once per row only when the rows differ: a project name, or the
  // machine for machine-wide rows.
  const places = useMemo(() => {
    const where = shown.needs.map((i) => (i.projectId ? (projects[i.projectId]?.name ?? i.where) : i.where));
    const show = new Set(where).size > 1;
    return new Map(shown.needs.map((i, n) => [i.key, show ? where[n]! : null]));
  }, [shown, projects]);
  // ORGU-92: an incident Orgus handles still says a repeated row once (above), but sits apart and never counts.
  const split = useMemo(() => splitHandled(shown.incidents), [shown]);
  const groups = useMemo(() => needsGroups(shown.needs, split.person), [shown, split]);
  const count = rowCount(groups);
  // Two labelled parts, so "Needs you" never carries two totals: what only you can decide (the
  // Inbox's number, the tile's and the rail's, from the one selector), then what to check
  // (security alerts and problems).
  const decide = useWaitingCount();
  const asks = groups.filter((g) => ASK_GROUPS.has(g.group)).reduce((n, g) => n + g.rows.length, 0);
  const check = Math.max(0, count - asks);
  const [open, setOpen] = useState<Set<string>>(() => new Set(linked ? [linked] : []));
  const [all, setAll] = useState<Set<NeedGroup>>(() => new Set());
  // A notice's "View incident" opens its row, even past its group's first few; the row then
  // scrolls itself into view.
  const place = linkedRow(groups, linked);
  const linkedHandled = !!linked && split.orgus.some((i) => i.id === linked);
  const [handledOpen, setHandledOpen] = useState(linkedHandled);
  // Never a heading cut off from its rows: the groups past the budget fold, and a linked row opens them.
  const rest = restGroups(groups);
  const [moreOpen, setMoreOpen] = useState(false);
  const allGroups = moreOpen || (!!place && rest.folded.some((g) => g.group === place.group));
  const visibleGroups = allGroups ? groups : rest.shown;
  useEffect(() => {
    if (!linkedHandled || !linked) return;
    setHandledOpen(true);
    setOpen((s) => toggled(s, linked, true));
  }, [linked, linkedHandled]);
  useEffect(() => {
    if (!place || !linked) return;
    setOpen((s) => toggled(s, linked, true));
    if (place.showAll) setAll((s) => toggled(s, place.group, true));
  }, [linked, place?.group, place?.showAll]); // eslint-disable-line react-hooks/exhaustive-deps
  const x: Shared = {
    places,
    now,
    me,
    can,
    open,
    linked: place || linkedHandled ? linked : null,
    toggle: (id) => setOpen((s) => toggled(s, id, !s.has(id))),
  };
  const critical = split.person.some((i) => i.state === 'open' && i.severity === 'critical') || shown.needs.some((i) => i.kind === 'securityCritical' || i.kind === 'problem-critical');
  return (
    <Panel
      icon={<Hand />}
      iconTone={critical ? 'danger' : 'attention'}
      title="Needs you"
      // The header carries one number, the Inbox's; the groups below count the alerts and problems.
      sub={
        count ? (
          <span className="whitespace-nowrap" title={[decide && `${plural(decide, 'decision')} (as in the Inbox)`, check && `${plural(check, 'alert or problem', 'alerts and problems')} to check`].filter(Boolean).join(', ')}>
            <LiveNumber value={decide || check} /> {decide ? 'to decide' : 'to check'}
          </span>
        ) : undefined
      }
      actions={<WatchCaption view={view} stale={stale} />}
      flush
      className={className}
      data-section="needs-you"
    >
      {count === 0 ? (
        <EmptyState
          icon={<CircleCheckBig />}
          title="Nothing needs you"
          description={split.orgus.length ? 'Orgus is handling the rest.' : 'Approvals, questions and problems show up here.'}
        />
      ) : (
        <div className="flex flex-col gap-2 px-1 pb-1">
          {visibleGroups.map((g) => (
            <Group key={g.group} group={g.group} rows={g.rows} all={all.has(g.group)} onAll={(on) => setAll((s) => toggled(s, g.group, on))} x={x} />
          ))}
          {rest.folded.length > 0 && (
            <Pressable
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={allGroups}
              title={rest.folded.map((g) => NEED_GROUP_LABEL[g.group]).join(', ')}
              className="flex h-8 w-full items-center gap-2 rounded-control px-3 text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted"
            >
              <ChevronDown className={cn('size-3 transition-transform duration-(--duration-fast)', allGroups && 'rotate-180')} aria-hidden />
              {allGroups ? 'Show fewer groups' : `${plural(rest.folded.reduce((n, g) => n + g.rows.length, 0), 'more item')} in ${plural(rest.folded.length, 'group')}`}
            </Pressable>
          )}
        </div>
      )}
      {/* Every decision, in full, is one quiet step away. */}
      {decide > 0 && (
        <div className="flex px-4 pb-2 pt-1">
          <Link to="/inbox" className="flex items-center gap-1 rounded-control text-caption text-fg-subtle transition-colors duration-(--duration-fast) hover:text-fg-muted">
            Open inbox
            <ArrowRight className="size-3" aria-hidden />
          </Link>
        </div>
      )}
      {split.orgus.length > 0 && <Handled incidents={split.orgus} open={handledOpen} onOpen={setHandledOpen} x={x} />}
      {view.kind === 'error' && (
        <div className="px-3 pb-3">
          <ErrorState variant="inline" error={view.error} onRetry={view.retry} />
        </div>
      )}
    </Panel>
  );
}
